/* =============================================================
   vp-comparison-table-v2.js
   Place in: assets/vp-comparison-table-v2.js

   Custom always-visible scrollbar for the comparison table on
   mobile. Native mobile scrollbars auto-hide after touch scrolling
   ends (confirmed on Android, expected on iOS too — this is
   OS-level overlay-scrollbar behavior that CSS can't reliably
   override), so this builds a real, always-visible track+thumb
   element instead and keeps it in sync with the table's actual
   scroll position via JS.

   The thumb is also draggable (pointer events, works for touch and
   mouse alike), and clicking the track jumps to that position —
   both expected behaviors for something that looks like a
   scrollbar.
   ============================================================= */

(function () {
  'use strict';

  function initScrollbar(root) {
    if (root._vpCtScrollbarInit) return;
    root._vpCtScrollbarInit = true;

    var scrollEl = root.querySelector('[data-vp-ct-scroll]');
    var track = root.querySelector('[data-vp-ct-scrollbar-track]');
    var thumb = root.querySelector('[data-vp-ct-scrollbar-thumb]');
    if (!scrollEl || !track || !thumb) return;

    function update() {
      var scrollWidth = scrollEl.scrollWidth;
      var clientWidth = scrollEl.clientWidth;

      // Nothing to scroll — hide the bar entirely rather than show a
      // full-width, functionally useless one.
      if (scrollWidth <= clientWidth + 1) {
        track.removeAttribute('data-visible');
        return;
      }
      track.setAttribute('data-visible', 'true');

      // Floor the thumb width so it stays visible/grabbable even when
      // the table is much wider than the viewport.
      var thumbWidthPct = Math.max((clientWidth / scrollWidth) * 100, 8);
      var maxScrollLeft = scrollWidth - clientWidth;
      var scrollProgress = maxScrollLeft > 0 ? scrollEl.scrollLeft / maxScrollLeft : 0;
      var maxThumbLeftPct = 100 - thumbWidthPct;
      var thumbLeftPct = scrollProgress * maxThumbLeftPct;

      thumb.style.width = thumbWidthPct + '%';
      thumb.style.left = thumbLeftPct + '%';
    }

    // Keep the indicator synced when the table itself is scrolled
    // directly (touch swipe on the table).
    var ticking = false;
    scrollEl.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }, { passive: true });

    // Dragging the thumb scrolls the table.
    var dragging = false;
    var dragStartX = 0;
    var dragStartScrollLeft = 0;

    thumb.addEventListener('pointerdown', function (e) {
      dragging = true;
      dragStartX = e.clientX;
      dragStartScrollLeft = scrollEl.scrollLeft;
      thumb.setPointerCapture(e.pointerId);
    });

    thumb.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var deltaX = e.clientX - dragStartX;
      var scrollWidth = scrollEl.scrollWidth;
      var clientWidth = scrollEl.clientWidth;
      var maxScrollLeft = scrollWidth - clientWidth;
      var draggableTrackWidth = track.clientWidth - thumb.clientWidth;
      if (draggableTrackWidth <= 0) return;
      var scrollDelta = (deltaX / draggableTrackWidth) * maxScrollLeft;
      scrollEl.scrollLeft = dragStartScrollLeft + scrollDelta;
    });

    function endDrag(e) {
      dragging = false;
      try { thumb.releasePointerCapture(e.pointerId); } catch (err) { /* no-op */ }
    }
    thumb.addEventListener('pointerup', endDrag);
    thumb.addEventListener('pointercancel', endDrag);

    // Clicking the track (not the thumb itself) jumps to that position.
    track.addEventListener('click', function (e) {
      if (e.target === thumb) return;
      var trackRect = track.getBoundingClientRect();
      var clickPct = (e.clientX - trackRect.left) / trackRect.width;
      var maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
      scrollEl.scrollLeft = clickPct * maxScrollLeft;
    });

    update();
    window.addEventListener('resize', update);
  }

  function initAll() {
    document.querySelectorAll('.vp-ct').forEach(initScrollbar);
  }

  if (document.readyState === 'complete') {
    initAll();
  } else {
    window.addEventListener('load', initAll, { once: true });
  }

  /* Shopify editor: section dropped onto the page / re-rendered */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.vp-ct');
    if (root) initScrollbar(root);
  });

})();